import { CalendarClock, CarFront, ChevronRight, MapPin, TriangleAlert } from 'lucide-react';
import { useTimeFormat } from '../../../lib/tenantTime';
import { bookingRef, bookingTone, pickupLabel } from '../bookingFormat';
import { JobLink } from './JobLink';
import type { SubjectBooking } from '../../../api/types';
import { type LastReservationState, type LatestRead, lastReservationState, subjectNamed } from './lastReservation';

/**
 * What the customer behind this conversation last booked — one line under the
 * three-pane layout's header, on every channel.
 *
 * The mail layout answers this in the strip under its subject (MailContact);
 * here the header already says who the person is, so the strip is only the
 * reservation, and it is there without opening the contact panel — an agent
 * replying to "running late, still on for 6?" on SMS needs the pickup, not a
 * click. It is the same read the list chip makes for its page of rows
 * (GET /contacts/latest_bookings, for this one contact): the booking most
 * recently MADE, which the panel's pickup-ordered list cannot answer.
 *
 * Drawn only for a contact LINKED to a dispatch customer or affiliate desk — on
 * any channel, that is the "wherever possible": an email or phone number
 * dispatch matched, or one an agent linked by hand. An
 * unlinked contact has no bookings to look up, and a strip saying so on every
 * Telegram chat would be noise the contact panel already covers. Every other
 * empty is said in words (lastReservationState), never drawn as a blank line.
 *
 * A mail thread whose SUBJECT names a reservation shows that one instead,
 * linked contact or not (`named`): a reply to "Booking Confirmation # 11410" is
 * about 11410 whatever was booked since, and a stranger quoting it names the
 * trip as surely as a customer does. `pending` holds the strip on its skeleton
 * while that read is out, so it does not flash the last reservation first.
 */
export function ThreadReservation({
  latest,
  named = null,
  pending = false,
  onOpenBookings,
}: {
  latest: LatestRead;
  /** The reservation the thread's subject names, when it names one. */
  named?: SubjectBooking | null;
  /** The subject read is still out. */
  pending?: boolean;
  /** Open the contact panel at the full list — absent while it is already open. */
  onOpenBookings?: () => void;
}) {
  const { zone, formats } = useTimeFormat();
  // `true`: the caller draws the last reservation only for a linked contact,
  // so a silent read is a contact the service would not answer for, never
  // "not linked".
  const state: LastReservationState = named
    ? { kind: 'booking', booking: named.booking }
    : pending
      ? { kind: 'loading' }
      : lastReservationState(latest, true);
  const label = named ? 'In the subject' : 'Last reservation';
  const booking = state.kind === 'booking' ? state.booking : null;
  const route = booking ? [booking.pickup, booking.dropoff].filter(Boolean).join(' → ') : '';
  const tone = booking ? bookingTone(booking.status.state) : null;

  return (
    <div className="thr-trip">
      <span className="thr-trip-label" title={named ? subjectNamed(named) : label}>
        <CarFront size={12} />
        <span className="thr-trip-label-text">{label}</span>
      </span>
      {state.kind === 'loading' && <span className="sk thr-trip-sk" aria-busy />}
      {state.kind === 'warn' && (
        <span className="thr-trip-warn">
          <TriangleAlert size={12} />
          <span>{state.text}</span>
        </span>
      )}
      {state.kind === 'none' && <span className="thr-trip-none">{state.text}</span>}
      {booking && tone && (
        <>
          <JobLink kind={booking.kind} id={booking.id} className="thr-trip-ref" icon>
            {bookingRef(booking)}
          </JobLink>
          <span className="cv-row-role" style={{ background: tone.bg, color: tone.fg }}>
            {booking.status.label}
          </span>
          <span className="thr-trip-when">
            <CalendarClock size={11} />
            {pickupLabel(booking.pickupAt, booking.timeZone ?? zone, formats)}
          </span>
          {/* The text keeps a box of its own: a flex row cannot ellipsis a bare
              text node, and an airport address will not fit. */}
          {route && (
            <span className="thr-trip-leg" title={route}>
              <MapPin size={11} />
              <span className="thr-trip-clip">{route}</span>
            </span>
          )}
          {booking.vehicle && (
            <span className="thr-trip-car" title={booking.vehicle}>
              <CarFront size={11} />
              <span className="thr-trip-clip">{booking.vehicle}</span>
            </span>
          )}
          {onOpenBookings && (
            <button type="button" className="thr-trip-all" onClick={onOpenBookings}>
              All reservations
              <ChevronRight size={11} />
            </button>
          )}
        </>
      )}
    </div>
  );
}
